import { useParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { articles, type Article } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import ArticleCard from "@/components/base/ArticleCard";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState<string>(slug || "corporate-insider");

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const category = categories.find((c) => c.slug === activeSlug);
  const categoryArticles: Article[] = articles.filter((a) => a.categorySlug === activeSlug);

  const pageTitle = category ? `${category.name} - 黑料社区` : "分类 - 黑料社区";
  const pageDesc = category
    ? `${category.description} 黑料社区深度揭秘${category.name}内幕，聚合真实爆料与独家情报。`
    : "黑料社区分类浏览";

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: pageTitle,
            description: pageDesc,
            url: `https://heiliao.community/category/${activeSlug}`,
            inLanguage: "zh-CN",
            isPartOf: {
              "@type": "WebSite",
              name: "黑料社区",
              url: "https://heiliao.community",
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "首页", item: "https://heiliao.community" },
                { "@type": "ListItem", position: 2, name: category?.name || "分类" },
              ],
            },
          }),
        }}
      />

      <Navbar />

      <main className="pt-20">
        <div className="container-custom py-8">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb
              items={[
                { name: "首页", path: "/" },
                { name: category?.name || "分类" },
              ]}
            />
          </div>

          {/* Category Header */}
          {category && (
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-500/10">
                  <i className={`${category.icon} text-primary-400 text-2xl`}></i>
                </div>
                <div>
                  <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-100">
                    {category.name}
                  </h1>
                  <p className="text-foreground-500 text-sm mt-1">{category.description}</p>
                </div>
              </div>
              <p className="text-foreground-600 text-xs">
                共 {categoryArticles.length} 篇相关爆料
              </p>
            </div>
          )}

          {/* Category Tabs */}
          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => window.scrollTo({ top: 0, behavior: "instant" })}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeSlug === cat.slug
                    ? "bg-primary-500/20 text-primary-400 ring-1 ring-primary-500/30"
                    : "bg-background-100 text-foreground-400 hover:text-foreground-200 hover:bg-background-200/50"
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${cat.icon} text-sm`}></i>
                </div>
                {cat.name}
                <span className={`text-xs ${activeSlug === cat.slug ? "text-primary-400/70" : "text-foreground-600"}`}>
                  {cat.articleCount}
                </span>
              </Link>
            ))}
          </div>

          {/* Articles */}
          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-inbox-line text-4xl text-foreground-600"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无文章</p>
            </div>
          )}

          {/* Back Link */}
          <div className="mt-12 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-foreground-500 hover:text-primary-400 text-sm transition-colors"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              返回首页
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}